import { computed, ref, Ref } from 'vue'
import { Employee } from '../../employees/models/employee'
import { Area, AreaID } from '../../areas/models/area'
import { Tag, TagMatchMode } from '../../tags/models/tag'
import { SkillID } from '../../skills/models/skill'
import { JobRoleID } from '../../job_roles/models/jobRole'

export type EmployeeSelectDialogResult = {
    employees: Employee[]
    filters?: {
        tags: Tag[]
        tagMatchMode: TagMatchMode
    }
}

export type EmployeeSelectDialogState = {
    areaId: AreaID | null
    skillIds: SkillID[]
    jobRoleId: JobRoleID | null
    employees: Employee[]
    disabledEmployeeIds: Employee['id'][]
    visible: boolean
    resolve?: (value: EmployeeSelectDialogResult | undefined) => void
    reject?: () => void
}

const state: Ref<EmployeeSelectDialogState> = ref({
    areaId: null,
    skillIds: [],
    jobRoleId: null,
    employees: [],
    disabledEmployeeIds: [],
    visible: false,
})

/**
 * Composable for managing the Employee Select Dialog.
 * 
 * Provides a method to show the dialog and handle user selection or cancellation.
 *  If confirmed, returns the selected employees. If cancelled, returns undefined.
 */
export function useEmployeeSelectDialog() {
    const show = async (data: { employees: Employee[], disabledEmployeeIds: Employee['id'][], area: Area, skillIds: SkillID[], jobRoleId?: JobRoleID | null }): Promise<EmployeeSelectDialogResult | undefined> => {
        return new Promise((resolve, reject) => {
            state.value = {
                areaId: data.area.id,
                skillIds: data.skillIds,
                jobRoleId: data.jobRoleId ?? null,
                employees: data.employees,
                disabledEmployeeIds: data.disabledEmployeeIds,
                visible: true,
                resolve,
                reject
            }
        })
    }

    const hide = () => {
        state.value.resolve?.(undefined)
        resetState()
    }

    const confirm = (selectedEmployees: Employee[], filters?: { tags: Tag[], tagMatchMode: TagMatchMode }) => {
        state.value.resolve?.({ employees: selectedEmployees, filters })
        resetState()
    }

    const cancel = () => {
        state.value.reject?.()
        resetState()
    }

    const resetState = () => {
        state.value = {
            areaId: null,
            skillIds: [],
            jobRoleId: null,
            employees: [],
            disabledEmployeeIds: [],
            visible: false,
        }
    }

    const addEmployees = (employees: Employee[]) => {
        state.value.employees = [...state.value.employees, ...employees]
    }

    const removeEmployee = (employee: Employee) => {
        state.value.employees = state.value.employees.filter((e) => e.id !== employee.id)
    }

    const removeAll = () => {
        state.value.employees = []
    }

    const isShown = computed(() => state.value.visible)

    const employees = computed(() => state.value.employees)

    const areaId = computed(() => state.value.areaId)

    const skillIds = computed(() => state.value.skillIds)

    const jobRoleId = computed(() => state.value.jobRoleId)

    return {
        show,
        hide,
        confirm,
        cancel,
        addEmployees,
        removeEmployee,
        removeAll,
        isShown,
        employees,
        disabledEmployeeIds: computed(() => state.value.disabledEmployeeIds),
        areaId,
        skillIds,
        jobRoleId,
    }
}